import { useLocation, useNavigate } from '@remix-run/react';
import { NAVIGATION_CONTAINER_IDENTIFIER } from '@shared/dream-components';
import cx from 'classnames';
import { useEffect, useState } from 'react';

import PostLikeAction from '~/components/Actions/PostLikeAction';
import HeroChatIcon from '~/components/Icons/HeroChatIcon';

import type { Admin } from '~/interfaces/admin';
import type { Post } from '~/interfaces/post';
import type { Subscription } from '~/interfaces/subscriptions';

import useIsMobile from '~/hooks/useIsMobile';
import usePostInsights from '~/hooks/useLoaders/usePostInsights';
import useScrollDetection from '~/hooks/useScrollDetection';

import BackButton from './BackButton';
import SocialSharer from './SocialSharer';

interface Props {
  subscription?: Subscription;
  admin?: Admin;
  post: Post;
}

const CommentCounter = ({ count }: { count?: number }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleToComments = () => {
    navigate(`${location.pathname}#comments`);
  };

  return (
    <button onClick={handleToComments} className="group relative top-[1px] flex items-center outline-none md:pt-0">
      <div className="rounded-full p-1 hover:bg-[#f3f4f6]">
        <HeroChatIcon
          className={cx('h-7 w-7 stroke-1 text-wt-text-on-background opacity-50 group-hover:opacity-100')}
        />
      </div>

      <span
        className={cx(
          count === 0 ? 'hidden' : 'text-[#6B7280]',
          'relative -top-[1px] text-sm font-medium group-hover:opacity-100 md:top-0',
        )}
      >
        {count}
      </span>
    </button>
  );
};

const FloatingActions = ({ subscription, post, admin }: Props) => {
  const [url, setUrl] = useState('');
  const [fromTop, setFromTop] = useState<number | undefined>();
  const postInsights = usePostInsights(post.id);
  const isMobile = useIsMobile();
  const { isScrolling } = useScrollDetection({
    delay: 300,
    enabled: isMobile,
  });
  const hideActions = isScrolling && isMobile;

  useEffect(() => {
    setUrl(window.location.href);
    const navElement = window.document.querySelector(`#${NAVIGATION_CONTAINER_IDENTIFIER}`);
    if (navElement && !isMobile) {
      setFromTop(navElement.clientHeight);
    } else {
      setFromTop(undefined);
    }
  }, [isMobile]);

  return (
    <div
      className={cx(
        'fixed bottom-0 left-0 z-20 w-full rounded bg-wt-background shadow-xl transition-all duration-300 ease-in-out md:bottom-auto md:z-auto md:!w-fit md:border-none md:shadow-none',
        hideActions ? 'h-0 overflow-hidden opacity-0' : 'opacity-100',
      )}
      style={{
        top: fromTop && `${fromTop}px`,
      }}
    >
      <div className="absolute left-0 top-0 w-full border border-t border-wt-text-on-background bg-wt-background opacity-10 md:hidden" />
      <div className="mx-auto w-full max-w-6xl px-0 lg:px-4">
        <div className={cx('flex flex-col gap-8 md:h-40')}>
          <div className="">
            <div className="grid grid-cols-3 p-4 px-8 sm:p-2 sm:px-2 md:grid-cols-1 md:gap-2">
              <div className={cx('relative flex flex-col justify-center md:left-1')}>
                <BackButton />
              </div>
              <div className="flex items-center justify-center gap-3 md:flex-col md:items-start md:gap-1">
                <PostLikeAction
                  className="h-7 w-7 outline-none"
                  post={post}
                  likes={postInsights.likes}
                  fetched={postInsights.fetched}
                  admin={admin}
                  subscription={subscription}
                />
                {post.comments_enabled && <CommentCounter count={postInsights.comment_count} />}
                <SocialSharer
                  title={post.web_title}
                  description={post.web_subtitle}
                  url={url}
                  iconClassName="text-wt-text-on-background opacity-50 hover:opacity-100"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloatingActions;
